import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Users, Calendar, TrendingUp, LogOut, Shield, ShieldCheck, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard({ user, setUser }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [backupRemaining, setBackupRemaining] = useState(0);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [setupData, setSetupData] = useState(null); // {qr_code, secret}
  const [verifyCode, setVerifyCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null); // Shown ONCE after activation/regenerate
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchStats();
    fetch2FAStatus();
  }, [user, navigate]);

  const fetch2FAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/2fa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTwoFAEnabled(data.enabled);
        setBackupRemaining(data.backup_codes_remaining || 0);
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.already_enabled) {
          setTwoFAEnabled(true);
          toast.info(data.message);
        } else {
          setSetupData(data);
        }
      } else {
        toast.error('No se pudo generar el QR');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleActivate2FA = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/2fa/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code: verifyCode })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('2FA activado correctamente');
        setTwoFAEnabled(true);
        setSetupData(null);
        setVerifyCode('');
        setBackupCodes(data.backup_codes || []);
        setBackupRemaining((data.backup_codes || []).length);
      } else {
        toast.error('Código incorrecto');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleRegenerateBackupCodes = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code: regenCode })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Códigos regenerados. Los antiguos ya no son válidos.');
        setBackupCodes(data.backup_codes || []);
        setBackupRemaining((data.backup_codes || []).length);
        setShowRegen(false);
        setRegenCode('');
      } else {
        toast.error('Código incorrecto');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTwoFALoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!backupCodes) return;
    const content = `ARTHUR FITZ - Códigos de recuperación 2FA\nGenerados: ${new Date().toLocaleString('es-ES')}\nCuenta: ${user?.email || ''}\n\nGuarda estos códigos en un lugar seguro. Cada uno puede usarse UNA SOLA VEZ.\n\n${backupCodes.join('\n')}\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'arthur-fitz-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyBackupCodes = async () => {
    if (!backupCodes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Códigos copiados al portapapeles');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code: disableCode })
      });
      if (response.ok) {
        toast.success('2FA desactivado');
        setTwoFAEnabled(false);
        setShowDisable(false);
        setDisableCode('');
        setBackupCodes(null);
        setBackupRemaining(0);
      } else {
        toast.error('Código incorrecto');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTwoFALoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6">
            <Link to="/admin" className="font-medium text-black border-b-2 border-black pb-2">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/abandoned-carts" className="text-gray-600 hover:text-black" data-testid="nav-admin-abandoned-carts">Carritos</Link>
            <Link to="/admin/loyalty" className="text-gray-600 hover:text-black">ArtFitz Club</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black">Galería</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Playfair Display' }}>Dashboard</h2>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 border-2 border-black">
            <div className="flex items-center justify-between mb-4">
              <Package className="w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.total_products || 0}</span>
            </div>
            <p className="text-gray-600">Total Productos</p>
          </div>

          <div className="bg-white p-6 border-2 border-black">
            <div className="flex items-center justify-between mb-4">
              <ShoppingCart className="w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.total_orders || 0}</span>
            </div>
            <p className="text-gray-600">Total Pedidos</p>
          </div>

          <div className="bg-white p-6 border-2 border-black">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.total_customers || 0}</span>
            </div>
            <p className="text-gray-600">Clientes</p>
          </div>

          <div className="bg-white p-6 border-2 border-black">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.total_revenue?.toFixed(2) || 0}€</span>
            </div>
            <p className="text-gray-600">Ingresos Totales</p>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white p-6 border-2 border-black mb-8">
          <h3 className="text-xl font-bold mb-4">Pedidos Recientes</h3>
          {stats?.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left p-3">ID Pedido</th>
                    <th className="text-left p-3">Cliente</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Estado</th>
                    <th className="text-left p-3">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map((order) => (
                    <tr key={order.id} className="border-b">
                      <td className="p-3">{order.id.substring(0, 8)}</td>
                      <td className="p-3">{order.user_name}</td>
                      <td className="p-3">{order.total.toFixed(2)}€</td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-gray-200 rounded text-xs">{order.order_status}</span>
                      </td>
                      <td className="p-3">{new Date(order.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No hay pedidos recientes</p>
          )}
        </div>

        {/* Security / 2FA Section */}
        <div className="bg-white p-6 border-2 border-black mb-8" data-testid="admin-security-section">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6" />
            <h3 className="text-xl font-bold">Seguridad de la Cuenta</h3>
          </div>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-2 mb-2">
                {twoFAEnabled ? (
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <ShieldOff className="w-5 h-5 text-gray-400" />
                )}
                <p className="font-semibold">
                  Verificación en Dos Pasos (2FA): {' '}
                  <span className={twoFAEnabled ? 'text-green-600' : 'text-gray-500'} data-testid="2fa-status-label">
                    {twoFAEnabled ? 'Activada' : 'Desactivada'}
                  </span>
                  {twoFAEnabled && (
                    <span className="ml-3 text-xs text-gray-500" data-testid="backup-codes-remaining">
                      · {backupRemaining} código{backupRemaining === 1 ? '' : 's'} de recuperación restantes
                    </span>
                  )}
                </p>
              </div>
              <p className="text-sm text-gray-600">
                Añade una capa extra de seguridad a tu cuenta de administrador usando una aplicación autenticadora (Google Authenticator, Authy, 1Password, etc.).
              </p>
            </div>

            <div>
              {!twoFAEnabled && !setupData && (
                <Button
                  onClick={handleSetup2FA}
                  disabled={twoFALoading}
                  className="bg-black text-white hover:bg-gray-800"
                  data-testid="enable-2fa-btn"
                >
                  {twoFALoading ? 'Generando...' : 'Activar 2FA'}
                </Button>
              )}
              {twoFAEnabled && !showDisable && (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => setShowRegen(true)}
                    variant="outline"
                    className="border-black text-black hover:bg-gray-100"
                    data-testid="regenerate-backup-codes-btn"
                  >
                    Regenerar códigos
                  </Button>
                  <Button
                    onClick={() => setShowDisable(true)}
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    data-testid="disable-2fa-btn"
                  >
                    Desactivar 2FA
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Setup QR flow */}
          {setupData && (
            <div className="mt-6 pt-6 border-t-2 border-gray-200" data-testid="2fa-setup-panel">
              <h4 className="font-bold mb-3">Paso 1: Escanea el QR</h4>
              <p className="text-sm text-gray-600 mb-4">
                Abre tu app autenticadora y escanea el siguiente código QR. Si no puedes escanear, introduce manualmente la clave: {' '}
                <code className="bg-gray-100 px-2 py-1 text-xs font-mono">{setupData.secret}</code>
              </p>
              <div className="flex justify-center mb-6">
                <img src={setupData.qr_code} alt="QR 2FA" className="border-2 border-black" style={{ maxWidth: '220px' }} data-testid="2fa-qr-image" />
              </div>

              <h4 className="font-bold mb-3">Paso 2: Introduce el código de 6 dígitos</h4>
              <form onSubmit={handleActivate2FA} className="flex gap-3 flex-wrap">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-xl tracking-widest font-mono h-12 border-2 border-black max-w-[200px]"
                  required
                  data-testid="2fa-verify-input"
                />
                <Button
                  type="submit"
                  disabled={twoFALoading || verifyCode.length !== 6}
                  className="bg-black text-white hover:bg-gray-800"
                  data-testid="2fa-activate-btn"
                >
                  {twoFALoading ? 'Verificando...' : 'Verificar y Activar'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setSetupData(null); setVerifyCode(''); }}
                >
                  Cancelar
                </Button>
              </form>
            </div>
          )}

          {/* Disable flow */}
          {showDisable && (
            <div className="mt-6 pt-6 border-t-2 border-gray-200" data-testid="2fa-disable-panel">
              <h4 className="font-bold mb-3">Desactivar 2FA</h4>
              <p className="text-sm text-gray-600 mb-4">
                Introduce un código actual de tu app autenticadora para confirmar la desactivación.
              </p>
              <form onSubmit={handleDisable2FA} className="flex gap-3 flex-wrap">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-xl tracking-widest font-mono h-12 border-2 border-black max-w-[200px]"
                  required
                  data-testid="2fa-disable-input"
                />
                <Button
                  type="submit"
                  disabled={twoFALoading || disableCode.length !== 6}
                  className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="2fa-disable-confirm-btn"
                >
                  {twoFALoading ? 'Desactivando...' : 'Confirmar Desactivación'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowDisable(false); setDisableCode(''); }}
                >
                  Cancelar
                </Button>
              </form>
            </div>
          )}

          {/* Regenerate backup codes flow */}
          {showRegen && (
            <div className="mt-6 pt-6 border-t-2 border-gray-200" data-testid="2fa-regen-panel">
              <h4 className="font-bold mb-3">Regenerar códigos de recuperación</h4>
              <p className="text-sm text-gray-600 mb-4">
                Introduce un código actual de tu app autenticadora. Se generarán 10 códigos nuevos y los anteriores dejarán de funcionar inmediatamente.
              </p>
              <form onSubmit={handleRegenerateBackupCodes} className="flex gap-3 flex-wrap">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={regenCode}
                  onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-xl tracking-widest font-mono h-12 border-2 border-black max-w-[200px]"
                  required
                  data-testid="2fa-regen-input"
                />
                <Button
                  type="submit"
                  disabled={twoFALoading || regenCode.length !== 6}
                  className="bg-black text-white hover:bg-gray-800"
                  data-testid="2fa-regen-confirm-btn"
                >
                  {twoFALoading ? 'Generando...' : 'Regenerar'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowRegen(false); setRegenCode(''); }}
                >
                  Cancelar
                </Button>
              </form>
            </div>
          )}

          {/* Backup codes display (ONE-TIME) */}
          {backupCodes && backupCodes.length > 0 && (
            <div className="mt-6 pt-6 border-t-2 border-gray-200" data-testid="backup-codes-panel">
              <div className="bg-yellow-50 border-2 border-yellow-500 p-4 mb-4">
                <h4 className="font-bold text-yellow-900 mb-2">⚠ Guarda estos códigos AHORA</h4>
                <p className="text-sm text-yellow-900">
                  Estos códigos solo se mostrarán una vez. Úsalos para recuperar el acceso si pierdes tu móvil.
                  Cada código sirve para <strong>un único inicio de sesión</strong>.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="backup-codes-list">
                {backupCodes.map((c, i) => (
                  <code
                    key={c || `backup-${i}`}
                    className="bg-gray-100 border border-gray-300 px-3 py-2 text-center font-mono text-sm tracking-wider"
                    data-testid={`backup-code-${i}`}
                  >
                    {c}
                  </code>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  onClick={downloadBackupCodes}
                  className="bg-black text-white hover:bg-gray-800"
                  data-testid="download-backup-codes-btn"
                >
                  Descargar .txt
                </Button>
                <Button
                  type="button"
                  onClick={copyBackupCodes}
                  variant="outline"
                  data-testid="copy-backup-codes-btn"
                >
                  Copiar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBackupCodes(null)}
                  data-testid="dismiss-backup-codes-btn"
                >
                  Ya los he guardado
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}